import { css } from '@emotion/react';
import {
  type FC, useCallback, useEffect, useRef, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { ButtonComponent } from '~/_shared/baseComponents/buttons/button/button.component';
import {
  createColor, guaranteeHash,
} from '~/_shared/components/colorPicker/colorPicker.helpers';
import { type ColorResult } from '~/_shared/components/colorPicker/colorPicker.types';
import { type RouteLeg } from '~/_shared/types/googleMaps/googleMaps.types';
import { catchApiError } from '~/_shared/utils/api/apiError.helpers';
import { getLast } from '~/_shared/utils/array/array.helpers';
import { zipWith } from '~/_shared/utils/collections/collections';
import { createUuid } from '~/_shared/utils/createUuid';
import { googleLatLngToLocal } from '~/_shared/utils/geolocation/geolocation';
import {
  type TranslationFnc, useTranslation,
} from '~/_shared/utils/hooks';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { logError } from '~/_shared/utils/logError';
import { isTextEmpty } from '~/_shared/utils/text/text.helpers';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { getWaypointId } from '~/directions/directions.helper';
import { useFindDirections } from '~/directions/useFindDirections.hook';
import { useModal } from '~/modal/useModal.hook';
import { useIsMobileScreenSelector } from '~/store/frontendState/deviceInfo/deviceInfo.selector';
import { mapComponentSetZoomToBounds } from '~/store/frontendState/mapComponent/mapComponent.actionCreators';
import {
  frontendStateDirectionsAddEmptyWaypoint, frontendStateDirectionsCancelScrollIntoViewpointsForm, frontendStateDirectionsClearWaypoints, frontendStateDirectionsRemoveWaypoint,
  frontendStateDirectionsSetWaypointAddress, frontendStateDirectionsSetWaypoints,
} from '~/store/frontendState/mapTools/directions/directions.actionCreators';
import {
  useDirectionsLastLoadedRouteSelector, useDirectionsScrollIntoViewpointsFormSelector,
} from '~/store/frontendState/mapTools/directions/directions.selectors';
import {
  type CompleteWaypoint, type Waypoint,
} from '~/store/frontendState/mapTools/directions/directions.state';
import {
  startRoutingLassoSelection, stopLassoTool,
} from '~/store/frontendState/mapTools/lasso/lasso.actionCreators';
import { useLassoStateSelector } from '~/store/frontendState/mapTools/lasso/lasso.selectors';
import { LassoConfiguration } from '~/store/frontendState/mapTools/lasso/lasso.state';
import { addOrUpdateRoute } from '~/store/mapSettings/directions/mapSettingsDirections.actionCreators';
import { useMapSettingsDirectionsSelectedCollapsingSelector } from '~/store/mapSettings/directions/mapSettingsDirections.selectors';
import { type MapSettingsRoute } from '~/store/mapSettings/directions/mapSettingsDirections.state';
import { createAppError } from '~/store/modal/modal.actionCreators';
import { useIsMapEditDisabled } from '~/store/selectors/useMapInfoSelectors';
import { useIsUserOverDirectionsApiLimit } from '~/store/userData/userData.selectors';
import { AppErrorType } from '../../../appError/appErrorType.enum';
import {
  DirectionsStatus,
  type DirectionsSuccessResult, maxRequestWaypoints, maxTotalWaypoints,
} from '../../getDirections.service';
import {
  getRouteColor, getRouteName,
} from '../directionsPanel.helpers';
import { DirectionsPanelFormComponent } from './directionsPanelForm.component';

type DirectionsPanelFormContainerProps = Readonly<{
  routes: ReadonlyArray<MapSettingsRoute>;
}>;

const confirmationModalStyle = css({ overflow: 'hidden' });

const reorderWaypointsIntoLegsOrder = (waypoints: ReadonlyArray<Waypoint>, legsWithNewOrder: DirectionsSuccessResult['legs']): ReadonlyArray<Waypoint> => {
  const waypointsMap = new Map(waypoints.map(w => [w.id, w] as const));
  return [...legsWithNewOrder, getLast(waypoints)]
    .map((waypoint) => waypoint && waypointsMap.get(waypoint.id) || null)
    .filter(notNullsy);
};

const completeWaypointsFromLegs = (routeId: Uuid, waypoints: ReadonlyArray<Waypoint>, legs: ReadonlyArray<RouteLeg>): ReadonlyArray<CompleteWaypoint> => {
  if (waypoints.length !== legs.length + 1) {
    throw new Error(`The amount of legs should be one less than the amount of waypoints. Waypoints: ${waypoints.length}, legs: ${legs.length}`);
  }
  const lastWaypoint = getLast(waypoints);
  const lastLeg = getLast(legs);
  if (!lastLeg) {
    throw new Error(`There's no last route leg. It's '${lastLeg}'.`);
  }
  if (!lastWaypoint) {
    throw new Error(`There's no last waypoint. It's '${lastWaypoint}'.`);
  }

  return zipWith(waypoints, legs, (waypoint, leg) => {
    return {
      ...waypoint,
      id: getWaypointId(routeId, waypoint.id),
      latLng: waypoint.latLng ?? googleLatLngToLocal(leg.start_location),
      address: waypoint.address ?? leg.start_address,
    };
  }).concat([{
    ...lastWaypoint,
    id: getWaypointId(routeId, lastWaypoint.id),
    latLng: lastWaypoint.latLng ?? googleLatLngToLocal(lastLeg.end_location),
    address: lastWaypoint.address ?? lastLeg.end_address,
  }]);
};

export const DirectionsPanelFormContainer: FC<DirectionsPanelFormContainerProps> = (props) => {
  const [routeColor, setRouteColor] = useState(() => getRouteColor(props.routes.map(route => createColor(route.color).hex)));
  const [isLoading, setIsLoading] = useState(false);
  const formElementRef = useRef<HTMLDivElement>(null);

  const scrollDirectionsIntoView = useDirectionsScrollIntoViewpointsFormSelector();
  const lassoState = useLassoStateSelector();
  const isEditModeDisabled = useIsMapEditDisabled();
  const isMobileScreen = useIsMobileScreenSelector();
  const [t] = useTranslation();
  const { findDirections } = useFindDirections();
  const dispatch = useDispatch();
  const routeCollapsingLevel = useMapSettingsDirectionsSelectedCollapsingSelector();
  const lastLoadedRouteId = useDirectionsLastLoadedRouteSelector();
  const routeToReplace = props.routes.find(r => r.id === lastLoadedRouteId);
  const overDirectionsApiUsage = useIsUserOverDirectionsApiLimit();

  const waypoints = useSelector(state => state.frontendState.mapTools.directions.waypoints);
  const shouldBeginAtUserLocation = useSelector(state => state.map.mapSettings.data.directions.shouldBeginAtUserLocation);

  const isLassoActive = lassoState.isActive && lassoState.configuration === LassoConfiguration.RoutingSelection;

  useEffect(() => {
    setRouteColor(getRouteColor(props.routes.map(route => createColor(route.color).hex)));
  }, [props.routes]);

  const { openModal: directionsPanelFormAddModal } = useModal('DirectionsPanelFormAdd');
  const { openModal: openDirectionSettingsModal } = useModal('DirectionSettings');
  const { openModal: openAdHocModal } = useModal('AdHoc');

  const openDirectionSettings = () => openDirectionSettingsModal({});

  const addNewLocation = () => dispatch(frontendStateDirectionsAddEmptyWaypoint());

  const removeWaypoint = (waypointIndex: number) => dispatch(frontendStateDirectionsRemoveWaypoint(waypointIndex));

  const setWaypointAddress = (index: number, value: string) => dispatch(frontendStateDirectionsSetWaypointAddress(index, value));

  const clearEndpoints = () => dispatch(frontendStateDirectionsClearWaypoints());

  const setWaypoints = (waypoints: ReadonlyArray<Waypoint>) => dispatch(frontendStateDirectionsSetWaypoints(waypoints, shouldBeginAtUserLocation));

  const changeRouteColor = (color: ColorResult) => setRouteColor(color.hex);

  const showUnknownErrorModal = () => dispatch(createAppError({
    type: AppErrorType.General,
    title: t('No route found.'),
    errorTitle: t('Failed to find route for unknown reason.'),
  }));

  const showNotFoundErrorModal = () => dispatch(createAppError({
    type: AppErrorType.General,
    title: t('No route found.'),
    errorTitle: t('We could not find any route for your locations.'),
  }));

  const showInvalidAddressErrorModal = (invalidAddresses: ReadonlyArray<string>) => dispatch(createAppError({
    type: AppErrorType.General,
    title: t('Issue finding locations'),
    content: (
      <div>
        <p>{t('We were unable to find locations of some of your waypoints.')}</p>
        <ul>
          {invalidAddresses.map((addr, i) => (<li key={i}>{addr}</li>))}
        </ul>
        <p>{t('Please fix the waypoints addresses and try again.')}</p>
      </div>
    ),
  }));

  const replaceLastLoadedRoute = (result: DirectionsSuccessResult) => {
    if (routeToReplace) {
      addOrReplaceRoute(result, {
        ...routeToReplace,
        color: guaranteeHash(routeColor),
        waypoints: completeWaypointsFromLegs(
          routeToReplace.id,
          reorderWaypointsIntoLegsOrder(waypoints, result.legs),
          result.legs.map(l => l.data)
        ),
      });
    }
    else {
      addNewRouteInstance(result);
    }
  };

  const addNewRouteInstance = (result: DirectionsSuccessResult) => {
    const routeId = createUuid();
    addOrReplaceRoute(result, {
      id: routeId,
      color: guaranteeHash(routeColor),
      name: getRouteName(props.routes.length + 1, t),
      waypoints: completeWaypointsFromLegs(
        routeId,
        reorderWaypointsIntoLegsOrder(waypoints, result.legs),
        result.legs.map(l => l.data)
      ),
    });
  };

  const addOrReplaceRoute = (result: DirectionsSuccessResult, route: MapSettingsRoute) => {
    dispatch(addOrUpdateRoute({
      route,
      routeUiData: {
        expandedLegsIndexes: new Set(),
        legs: result.legs.map(l => l.data),
        copyrights: result.copyrights,
        apiResponses: result.apiResponses,
      },
      routeCollapsingLevel,
    }));
  };

  const handleAddOrReplaceRoute = (optimize: boolean, isConfirmed = false) => {
    if (optimize && waypoints.length > maxRequestWaypoints && !isConfirmed) {
      return confirmApproximations();
    }
    setIsLoading(true);
    const routeFinder = () => findDirections([{ waypoints, optimize, shouldReplaceFirstWithUserLocation }])
      .then(findDirectionsResults => new Promise<DirectionsSuccessResult>((resolve, reject) => {
        const result = findDirectionsResults[0];
        if (!result) {
          throw new Error('Missing directions result');
        }
        if (result.success) {
          resolve(result.success);
        }
        else {
          switch (result.error.status) {
            case DirectionsStatus.ZeroResults:
              showNotFoundErrorModal();
              return reject(result.error);

            case DirectionsStatus.NotFound:
              showInvalidAddressErrorModal(result.error.failedAddresses);
              return reject(result.error);

            default:
              showUnknownErrorModal();
              logError('Failed to find route for unknown reason', result.error);
              return reject(result.error);
          }
        }
      }))
      .then(updateWaypointOrder)
      .then((res) => {
        dispatch(mapComponentSetZoomToBounds(res.bounds));
        return res;
      })
      .catch((e) => {
        if (e instanceof GeolocationPositionError) {
          dispatch(createAppError({
            errorTitle: t('We were not able to find your location. Please check your browser settings and try again.'),
            title: t('Unable To Find Your Location'),
            type: AppErrorType.General,
          }));

          return;
        }

        throw e;
      })
      .catch(catchApiError(apiError => {
        dispatch(createAppError({
          type: AppErrorType.General,
          title: t('Something went wrong, please try again.'),
          content: apiError.message,
        }));
      }))
      .finally(() => setIsLoading(false));

    if (props.routes.length === 0 || !routeToReplace) {
      routeFinder().then(addNewRouteInstance).then(clearEndpoints);
    }
    else {
      directionsPanelFormAddModal({
        routeToReplaceName: routeToReplace.name,
        onAddAnother: () => routeFinder().then(addNewRouteInstance).then(clearEndpoints),
        onReplace: () => routeFinder().then(replaceLastLoadedRoute).then(clearEndpoints),
        onCancel: () => setIsLoading(false),
      });
    }
  };

  const confirmApproximations = () => openAdHocModal({
    caption: t('Soft limit of {{count}} stops reached', { count: maxRequestWaypoints }),
    content: onClose => (
      <div css={confirmationModalStyle}>
        <p>{t('directionsOverSoftLimitConfirmation{{count}}', { count: maxRequestWaypoints })}</p>
        <ButtonComponent
          text={t('Confirm')}
          onClick={() => {
            onClose();
            handleAddOrReplaceRoute(true, true);
          }}
        />
      </div>
    ),
  });

  const shouldReplaceFirstWithUserLocation = shouldBeginAtUserLocation && !waypoints[0]?.address?.trim();

  const updateWaypointOrder = (directionsResultWithNewOrder: DirectionsSuccessResult) => {
    setWaypoints(reorderWaypointsIntoLegsOrder(waypoints, directionsResultWithNewOrder.legs));
    return directionsResultWithNewOrder;
  };

  const getDirections = () => handleAddOrReplaceRoute(false);
  const getOptimizedDirections = () => handleAddOrReplaceRoute(true);

  const isClearFormEnabled = waypoints
    .filter(point => !isTextEmpty(point.address ?? '') || point.latLng !== null)
    .length > 0 || waypoints.length > 2;

  const errorMessage = overDirectionsApiUsage ? t('directionsTool.errors.overUsageLimit') : undefined;

  const directionsDisabledMessage = createGetDirectionsDisabledMessage({
    waypoints,
    shouldBeginAtUserLocation,
    overDirectionsApiUsage,
    t,
  });

  const startLassoSelection = useCallback(() => {
    dispatch(startRoutingLassoSelection());
  }, [dispatch]);

  const stopLassoToolSelection = useCallback(() => {
    dispatch(stopLassoTool());
  }, [dispatch]);

  useEffect(() => {
    if (scrollDirectionsIntoView) {
      formElementRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      dispatch(frontendStateDirectionsCancelScrollIntoViewpointsForm());
    }
  }, [dispatch, scrollDirectionsIntoView]);

  return (
    <DirectionsPanelFormComponent
      errorMessage={errorMessage}
      formElementRef={formElementRef}
      routeDirectionsSelectedColor={createColor(routeColor)}
      onOpenSettingsClick={openDirectionSettings}
      waypoints={waypoints}
      onAddLocationClick={addNewLocation}
      onWaypointCleanClick={removeWaypoint}
      setWaypointAddress={setWaypointAddress}
      onClearForm={clearEndpoints}
      setWaypoints={setWaypoints}
      onGetDirectionsClick={getDirections}
      onOptimizeRouteClick={getOptimizedDirections}
      onRouteColorChange={changeRouteColor}
      onLassoAndFillStart={startLassoSelection}
      onLassoAndFillCancel={stopLassoToolSelection}
      isClearFormEnabled={isClearFormEnabled}
      getDirectionsDisabledMessage={directionsDisabledMessage}
      startWithUserLocation={shouldBeginAtUserLocation}
      showAdminControls={!isEditModeDisabled}
      isLoading={isLoading}
      isLassoActive={isLassoActive}
      showLassoToolButton={!isMobileScreen}
    />
  );
};

const createGetDirectionsDisabledMessage = ({
  waypoints,
  shouldBeginAtUserLocation,
  overDirectionsApiUsage,
  t,
}: {
  waypoints: ReadonlyArray<Waypoint>;
  overDirectionsApiUsage: boolean;
  shouldBeginAtUserLocation: boolean;
  t: TranslationFnc;
}): string | null => {
  if (waypoints.length < 2) {
    return t('Please add at least 2 waypoints to continue.');
  }
  if (overDirectionsApiUsage) {
    return t('directionsTool.errors.overUsageLimit');
  }
  if (waypoints.length > maxTotalWaypoints) {
    return t(
      'Currently this tool supports maximum of {{limit}} locations. Please remove {{overLimit}} locations to continue.',
      { limit: maxTotalWaypoints, overLimit: waypoints.length - maxTotalWaypoints },
    );
  }
  const waypointsValidate = shouldBeginAtUserLocation ? waypoints.slice(1) : waypoints;
  const areWaypointsValid = waypointsValidate.every(point => !isTextEmpty(point.address ?? '') || point.latLng !== null);
  if (!areWaypointsValid) {
    return t('Some waypoint inputs are empty. Please fill in all the waypoints.');
  }

  return null;
};
